import React, { useCallback, useEffect, useRef, useState } from "react";
import { DetectionZone, Device, LaneLine, Ruler, ScreenLine, Spot, Vector2 } from "../../models";

export type Shape = Device | ScreenLine | LaneLine | DetectionZone | Spot | Ruler;

const joinClassNames = (...names: (string | undefined)[]) => names.filter(Boolean).join(" ");

// Helper functions for field of view calculations
const getFovPoint = (height: number, horizontalAngle: number, verticalAngle: number, scaleRatio: number): [number, number] => {
    const horizontalRad = (horizontalAngle * Math.PI) / 180;
    const verticalRad = (verticalAngle * Math.PI) / 180;
    const distance = height / Math.tan(verticalRad);
    const x = (distance * Math.tan(horizontalRad)) / scaleRatio;
    const y = distance / scaleRatio;
    return [x, y];
};

const getPathForFieldOfView = (x: number, y: number, height: number, elevationAngle: number, azimuthFov: number, elevationFov: number, scaleRatio: number) => {
    var [nearX, nearY] = getFovPoint(height, azimuthFov / 2, elevationAngle - elevationFov / 2, scaleRatio);
    var [farX, farY] = getFovPoint(height, azimuthFov / 2, elevationAngle + elevationFov / 2, scaleRatio);

    var maxDistance = 100 / scaleRatio;

    var nearY_clipped = Math.min(maxDistance, nearY);
    var farY_clipped = Math.min(maxDistance, farY);

    var pathCommands = [`M ${x + nearX} ${y + nearY_clipped}`, `L ${x + farX} ${y + farY_clipped}`, `L ${x - farX} ${y + farY_clipped}`, `L ${x - nearX} ${y + nearY_clipped}`, "Z"];

    return pathCommands.join(",");
};

const getRotation = (x: number, y: number, heading: number) => {
    var angle = heading - 180;
    return `rotate(${angle} ${x} ${y})`;
};

const getShapeCenter = (points: Vector2[]): Vector2 => {
    if (!points || points.length === 0) return new Vector2({ x: 0, y: 0 });
    const sumX = points.reduce((sum, p) => sum + (p?.x ?? 0), 0);
    const sumY = points.reduce((sum, p) => sum + (p?.y ?? 0), 0);
    return new Vector2({ x: sumX / points.length, y: sumY / points.length });
};

const getMidpoint = (p1: Vector2, p2: Vector2): Vector2 => {
    return new Vector2({
        x: ((p1?.x ?? 0) + (p2?.x ?? 0)) / 2,
        y: ((p1?.y ?? 0) + (p2?.y ?? 0)) / 2,
    });
};

const getAngleBetweenPoints = (p1: Vector2, p2: Vector2): number => {
    const dx = (p2?.x ?? 0) - (p1?.x ?? 0);
    const dy = (p2?.y ?? 0) - (p1?.y ?? 0);
    let angle = Math.atan2(-dy, dx) * (180 / Math.PI);
    if (angle < 0) angle += 360;
    return angle;
};

/**
 * Compass in/out directions for a screen line from its first two points.
 * Works for both image-pixel points (y grows down) and facility-meter points (y grows south).
 */
export const calculateScreenLineDirections = (p1: Vector2 | undefined, p2: Vector2 | undefined, reverse: boolean = false): { inbound: number; outbound: number } => {
    if (!p1 || !p2) return { inbound: 0, outbound: 0 };

    const lineAngle = getAngleBetweenPoints(p1, p2);
    let rightAngle = (lineAngle - 90 + 360) % 360;
    let leftAngle = (lineAngle + 90) % 360;

    const toCompass = (angle: number) => (90 - angle + 360) % 360;

    const inbound = reverse ? toCompass(leftAngle) : toCompass(rightAngle);
    const outbound = reverse ? toCompass(rightAngle) : toCompass(leftAngle);

    return {
        inbound: Math.round(inbound),
        outbound: Math.round(outbound),
    };
};

type SvgShapeProps = {
    color?: string;
    opacity?: number;
    shape: Shape;
    currentPosition?: Vector2;
    scaleRatio: number;
    additionalAttributes?: Map<string, object>;
    className?: string;
    showLabel?: boolean;
    showDirectionArrow?: boolean;
    deviceHeading?: number;
    isSelected?: boolean;
    onShapeClick?: (shapeId: string) => void;
    onVertexDrag?: undefined | ((e: MouseEvent | React.MouseEvent, shape: Shape, pointIndex: number) => any);
} & React.SVGProps<SVGPathElement | SVGCircleElement | SVGPolygonElement | SVGPolylineElement>;

export const SvgShape: React.FC<SvgShapeProps> = ({ color, opacity, shape, currentPosition, scaleRatio: scaleRotation, additionalAttributes, className: classNameIn, showLabel = false, showDirectionArrow = false, deviceHeading = 0, isSelected = false, onShapeClick, onVertexDrag, ...props }) => {
    const className = joinClassNames(classNameIn, shape.$type);
    const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
    const draggingIndexRef = useRef<number | null>(null);

    const getShapePoints = (points: Vector2[], additionalPoints?: Vector2[]) => {
        var allPoints = points.concat(additionalPoints ?? []);
        var coordinates = allPoints.map((point) => `${point?.x},${point?.y}`);
        return coordinates.join(" ");
    };

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!onVertexDrag || draggingIndexRef.current === null) return;
            onVertexDrag(e, shape, draggingIndexRef.current);
        },
        [onVertexDrag, shape],
    );

    const handleMouseUp = useCallback(() => {
        setDraggingIndex(null);
        draggingIndexRef.current = null;
    }, []);

    const handleVertexMouseDown = (e: React.MouseEvent, pointIndex: number) => {
        if (!onVertexDrag) return;
        e.stopPropagation();
        setDraggingIndex(pointIndex);
        draggingIndexRef.current = pointIndex;
        onVertexDrag(e, shape, pointIndex);
    };

    useEffect(() => {
        if (draggingIndex !== null) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
        }
        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [draggingIndex, handleMouseMove, handleMouseUp]);

    const renderVertexCircles = (points: Vector2[], strokeColor: string) => {
        if (!isSelected || !onVertexDrag || !points || points.length === 0) return null;

        return points.map((point, index) => <circle key={`vertex-${index}`} className="vertex-handle cursor-move" cx={point?.x ?? 0} cy={point?.y ?? 0} r={6} fill={draggingIndex === index ? "white" : strokeColor} stroke={strokeColor} strokeWidth={2} onMouseDown={(e) => handleVertexMouseDown(e, index)} style={{ cursor: "move" }} />);
    };

    const handleShapeClickInternal = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onShapeClick && shape.id) {
            onShapeClick(shape.id);
        }
    };

    const renderDetectionZoneDirectionArrow = (points: Vector2[], direction: number | undefined | null) => {
        if (!showDirectionArrow || direction === undefined || direction === null) return null;

        const center = getShapeCenter(points);
        const arrowLength = 40;
        const adjustedDirection = (direction - deviceHeading + 360) % 360;
        const angleRad = ((90 - adjustedDirection) * Math.PI) / 180;
        const endX = center.x + Math.cos(angleRad) * arrowLength;
        const endY = center.y - Math.sin(angleRad) * arrowLength; // Negative because Y is inverted

        return (
            <g className="direction-arrow">
                <line x1={center.x} y1={center.y} x2={endX} y2={endY} stroke="blue" strokeWidth={3} markerEnd="url(#arrowhead)" />
            </g>
        );
    };

    const renderScreenLineDirectionArrows = (screenLine: ScreenLine) => {
        if (!showDirectionArrow || !screenLine.points || screenLine.points.length < 2) return null;

        const p1 = screenLine.points[0];
        const p2 = screenLine.points[1];
        const midpoint = getMidpoint(p1, p2);
        const arrowLength = 30;

        const directions = calculateScreenLineDirections(p1, p2, screenLine.reverseInOut);

        const inboundAngleRad = ((90 - directions.inbound) * Math.PI) / 180;
        const outboundAngleRad = ((90 - directions.outbound) * Math.PI) / 180;

        const inboundEnd = {
            x: midpoint.x + Math.cos(inboundAngleRad) * arrowLength,
            y: midpoint.y - Math.sin(inboundAngleRad) * arrowLength,
        };
        const outboundEnd = {
            x: midpoint.x + Math.cos(outboundAngleRad) * arrowLength,
            y: midpoint.y - Math.sin(outboundAngleRad) * arrowLength,
        };

        return (
            <g className="direction-arrows">
                <line x1={midpoint.x} y1={midpoint.y} x2={inboundEnd.x} y2={inboundEnd.y} stroke="#22c55e" strokeWidth={3} markerEnd="url(#arrowhead-green)" />
                <line x1={midpoint.x} y1={midpoint.y} x2={outboundEnd.x} y2={outboundEnd.y} stroke="#f97316" strokeWidth={3} markerEnd="url(#arrowhead-orange)" />
            </g>
        );
    };

    const renderLaneLineDirectionArrow = (laneLine: LaneLine) => {
        if (!showDirectionArrow || !laneLine.points || laneLine.points.length < 2) return null;

        // Get the last two points to determine direction
        const lastIndex = laneLine.points.length - 1;
        const secondLastPoint = laneLine.points[lastIndex - 1];
        const lastPoint = laneLine.points[lastIndex];

        if (!secondLastPoint || !lastPoint) return null;

        // Calculate the direction angle from second-to-last to last point
        const dx = (lastPoint.x ?? 0) - (secondLastPoint.x ?? 0);
        const dy = (lastPoint.y ?? 0) - (secondLastPoint.y ?? 0);
        const angle = Math.atan2(dy, dx);

        // Use the lane line's color or default to yellow
        const arrowColor = laneLine.color || "#eab308";

        // Create arrowhead triangle points
        const arrowSize = 12;
        const arrowOffset = 8; // How far beyond the last point the tip extends
        const arrowAngle = Math.PI / 6; // 30 degrees for the arrowhead spread

        // Tip of the arrow extends beyond the last point
        const tipX = (lastPoint.x ?? 0) + Math.cos(angle) * arrowOffset;
        const tipY = (lastPoint.y ?? 0) + Math.sin(angle) * arrowOffset;

        // Left and right points of the arrowhead base (extend backwards from the tip)
        const leftX = tipX + Math.cos(angle + Math.PI - arrowAngle) * arrowSize;
        const leftY = tipY + Math.sin(angle + Math.PI - arrowAngle) * arrowSize;
        const rightX = tipX + Math.cos(angle + Math.PI + arrowAngle) * arrowSize;
        const rightY = tipY + Math.sin(angle + Math.PI + arrowAngle) * arrowSize;

        return (
            <g className="lane-direction-arrow">
                <polygon points={`${tipX},${tipY} ${leftX},${leftY} ${rightX},${rightY}`} fill={arrowColor} />
            </g>
        );
    };

    const renderLabel = (x: number, y: number, name: string | undefined) => {
        if (!showLabel || !name) return null;

        return (
            <text x={x} y={y - 10} textAnchor="middle" fill="black" stroke="white" strokeWidth={0.5} fontSize={12} fontWeight="bold" style={{ pointerEvents: "none" }}>
                {name}
            </text>
        );
    };

    const renderShape = (currentPosition?: Vector2) => {
        if (shape.$type === Device.$type) {
            const fov = shape as any as Device;
            var style = fov.color != null ? { fill: fov.color, stroke: fov.color } : {};
            const isFullyConfigured = fov.desiredTiltDegrees && fov.height;
            return (
                <>
                    <text x={fov.positionX} y={fov.positionY - 20} textAnchor="middle" stroke="black" fill="black" fontSize={15}>
                        {fov.name}
                    </text>
                    {isFullyConfigured && <path className="field-of-view" d={getPathForFieldOfView(fov.positionX, fov.positionY, fov.height ?? 0, fov.desiredTiltDegrees ?? 0, 90, 30, scaleRotation)} transform={getRotation(fov.positionX, fov.positionY, fov.headingDegrees ?? 0)} style={{ ...style, fillOpacity: 0.15 }} {...(props as React.SVGProps<SVGPathElement>)} />}

                    <circle className={joinClassNames(className, onVertexDrag ? "cursor-pointer" : undefined)} cx={fov.positionX} cy={fov.positionY} r={6} onMouseDown={(e) => handleVertexMouseDown(e, 0)} style={style} {...(props as React.SVGProps<SVGCircleElement>)} />
                </>
            );
        } else {
            const otherShape = shape as Exclude<Shape, Device>;
            const startingPoint = otherShape?.points ? otherShape.points[0] : undefined;
            const isDrawing = currentPosition !== undefined;
            const willClose = isDrawing && startingPoint === currentPosition;

            var svgStyle: React.CSSProperties = { fillOpacity: opacity ?? 0.3, strokeWidth: 2, stroke: color ?? "green" };

            if (otherShape.$type === ScreenLine.$type) {
                svgStyle.stroke = color ?? "red";
                svgStyle.strokeWidth = 4;
            }

            if (otherShape.$type === LaneLine.$type) {
                const laneLine = otherShape as LaneLine;
                svgStyle.stroke = laneLine.color || color || "#eab308"; // Use lane line's color or fallback to yellow
                svgStyle.fillOpacity = 0;
                svgStyle.strokeWidth = 4;
            }

            if (isSelected) {
                svgStyle.strokeWidth = (svgStyle.strokeWidth as number) + 2;
                svgStyle.filter = "drop-shadow(0 0 3px rgba(59, 130, 246, 0.8))";
            }

            const shapeClickProps = onShapeClick ? { onClick: handleShapeClickInternal, style: { ...svgStyle, cursor: "pointer", vectorEffect: "non-scaling-stroke" as const } } : { style: { ...svgStyle, vectorEffect: "non-scaling-stroke" as const } };

            const renderLineOrPolygon = () => {
                if (isDrawing && !willClose) {
                    return <polyline className={className} {...shapeClickProps} points={getShapePoints(otherShape.points ?? [], currentPosition ? [currentPosition] : [])} {...(props as React.SVGProps<SVGPolylineElement>)} />;
                } else if (shape.$type === LaneLine.$type || shape.$type === ScreenLine.$type) {
                    return <polyline className={className} {...shapeClickProps} points={getShapePoints(otherShape.points ?? [], currentPosition ? [currentPosition] : [])} {...(props as React.SVGProps<SVGPolylineElement>)} />;
                } else {
                    return <polygon className={className} {...shapeClickProps} style={{ ...shapeClickProps.style, fill: color ?? "green" }} points={getShapePoints(otherShape.points ?? [])} {...(props as React.SVGProps<SVGPolygonElement>)} />;
                }
            };

            const center = getShapeCenter(otherShape.points ?? []);

            const hasEnoughPointsForVertices = (shape.$type === DetectionZone.$type && (otherShape.points?.length ?? 0) >= 3) || (shape.$type === ScreenLine.$type && (otherShape.points?.length ?? 0) >= 2) || (shape.$type === LaneLine.$type && (otherShape.points?.length ?? 0) >= 2);

            return (
                <>
                    {renderLineOrPolygon()}
                    {shape.$type === DetectionZone.$type && renderDetectionZoneDirectionArrow(otherShape.points ?? [], (shape as DetectionZone).zoneCardinalDirectionDegrees)}
                    {shape.$type === ScreenLine.$type && renderScreenLineDirectionArrows(shape as ScreenLine)}
                    {shape.$type === LaneLine.$type && renderLaneLineDirectionArrow(shape as LaneLine)}
                    {renderLabel(center.x, center.y, otherShape.name)}
                    {hasEnoughPointsForVertices && renderVertexCircles(otherShape.points ?? [], svgStyle.stroke as string)}
                    {startingPoint && (isDrawing || otherShape.points?.length === 1) && <circle className="polygon-starting-point" style={{ ...svgStyle, fill: color ?? "red", stroke: color ?? "red" }} cx={startingPoint.x} r={3} cy={startingPoint.y} />}
                </>
            );
        }
    };

    return renderShape(currentPosition);
};

export { SvgArrowDefs } from "./SvgArrowMarkers";
